import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { getPartnerEmbedEntitlements } from '@/lib/embed/partnerEntitlements';

type ViewMode = 'default' | 'iframe';
export type EmbedDomainAuthority = 'not-applicable' | 'loading' | 'approved' | 'denied';

interface ViewModeContextValue {
  viewMode: ViewMode;
  isIframeView: boolean;
  isEmbedSignInEnabled: boolean;
  isWhitelabelRuntimeEnabled: boolean;
  embedDomainAuthority: EmbedDomainAuthority;
}

interface PartnerEmbedEntitlementRequest {
  key: string;
  partnerId: string;
  origin: string;
}

interface PartnerEmbedEntitlementState {
  requestKey: string;
  authority: 'approved' | 'denied';
  signInEnabled: boolean;
  whitelabelRuntimeEnabled: boolean;
}

const ViewModeContext = createContext<ViewModeContextValue | undefined>(undefined);

type GetSearchString = () => string;

const defaultGetSearchString: GetSearchString = () => {
  if (typeof window === 'undefined') return '';
  try {
    return window.location.search;
  } catch {
    return '';
  }
};

interface ViewModeProviderProps {
  children: ReactNode;
  /** Injected for tests; defaults to reading window.location.search */
  getSearchString?: GetSearchString;
}

// Store factory so tests can inject a custom search-string reader
// without relying on unmockable window.location mutations.
function createViewModeStore(getSearchString: GetSearchString) {
  return {
    subscribe(callback: () => void) {
      if (typeof window !== 'undefined') {
        window.addEventListener('popstate', callback);
      }
      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener('popstate', callback);
        }
      };
    },

    getSnapshot(): ViewMode {
      try {
        const params = new URLSearchParams(getSearchString());
        const view = params.get('view');
        return view === 'iframe' ? 'iframe' : 'default';
      } catch {
        return 'default';
      }
    },

    getServerSnapshot(): ViewMode {
      return 'default';
    },
  };
}

export const ViewModeProvider = ({
  children,
  getSearchString = defaultGetSearchString,
}: ViewModeProviderProps) => {
  const viewModeStore = useMemo(() => createViewModeStore(getSearchString), [getSearchString]);

  const viewMode = useSyncExternalStore(
    viewModeStore.subscribe,
    viewModeStore.getSnapshot,
    viewModeStore.getServerSnapshot
  );

  const partnerEmbedEntitlementRequest = useMemo<PartnerEmbedEntitlementRequest | null>(() => {
    if (viewMode !== 'iframe') return null;

    const params = new URLSearchParams(getSearchString());
    const partnerId = params.get('partner_id')?.trim();
    const origin = params.get('origin')?.trim();
    if (!partnerId || !origin) return null;

    return {
      key: `${partnerId}\u0000${origin}`,
      partnerId,
      origin,
    };
  }, [getSearchString, viewMode]);
  const hasPartnerEmbedIdentity = useMemo(() => {
    if (viewMode !== 'iframe') return false;
    return Boolean(new URLSearchParams(getSearchString()).get('partner_id')?.trim());
  }, [getSearchString, viewMode]);
  const [partnerEmbedEntitlementState, setPartnerEmbedEntitlementState] =
    useState<PartnerEmbedEntitlementState | null>(null);

  useEffect(() => {
    const request = partnerEmbedEntitlementRequest;
    if (!request) {
      setPartnerEmbedEntitlementState(null);
      return;
    }

    let cancelled = false;
    (async () => {
      const outcome = await getPartnerEmbedEntitlements({
        partnerId: request.partnerId,
        origin: request.origin,
      });
      if (cancelled) return;
      setPartnerEmbedEntitlementState({
        requestKey: request.key,
        authority: outcome.authority,
        signInEnabled: outcome.authority === 'approved' ? outcome.signInEnabled : false,
        whitelabelRuntimeEnabled:
          outcome.authority === 'approved' ? outcome.whitelabelRuntimeEnabled : false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [partnerEmbedEntitlementRequest]);

  const embedDomainAuthority: EmbedDomainAuthority =
    viewMode !== 'iframe' || !hasPartnerEmbedIdentity
      ? 'not-applicable'
      : !partnerEmbedEntitlementRequest
        ? 'denied'
        : partnerEmbedEntitlementState?.requestKey !== partnerEmbedEntitlementRequest.key
          ? 'loading'
          : partnerEmbedEntitlementState.authority;

  const effectiveEmbedSignInEnabled =
    embedDomainAuthority === 'approved'
      ? (partnerEmbedEntitlementState?.signInEnabled ?? false)
      : false;

  const value = useMemo<ViewModeContextValue>(
    () => ({
      viewMode,
      isIframeView: viewMode === 'iframe',
      isEmbedSignInEnabled: effectiveEmbedSignInEnabled,
      isWhitelabelRuntimeEnabled:
        embedDomainAuthority === 'approved'
          ? (partnerEmbedEntitlementState?.whitelabelRuntimeEnabled ?? false)
          : false,
      embedDomainAuthority,
    }),
    [
      viewMode,
      effectiveEmbedSignInEnabled,
      embedDomainAuthority,
      partnerEmbedEntitlementState?.whitelabelRuntimeEnabled,
    ]
  );

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useViewMode = (): ViewModeContextValue => {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
};
